import React from 'react';

import PropTypes from 'prop-types';

import { Icon } from '@eb/eds-icon';
import { CRUMBS_PROP_TYPE } from './constants';

import './breadcrumbs.scss';

import { ChevronRightChunky } from '@eb/eds-iconography';

const CrumbSeparator = ({ isLastCrumb }) => {
    let component = null;

    if (!isLastCrumb) {
        component = (
            <span className="eds-l-mar-hor-1">
                <Icon
                    type={<ChevronRightChunky />}
                    size="xsmall"
                    color="grey-400"
                />
            </span>
        );
    }

    return component;
};

const DisabledCrumb = ({ label }) => {
    let component = null;

    if (label) {
        component = (
            <span
                data-spec="breadcrumbs-disabled"
                className="eds-text-bm eds-text-color--grey-400"
            >
                {label}
            </span>
        );
    }

    return component;
};

const ActiveCrumb = ({ label }) => {
    let component = null;

    if (label) {
        component = (
            <span
                data-spec="breadcrumbs-active"
                className="eds-text-bm eds-text-color--grey-900"
            >
                {label}
            </span>
        );
    }

    return component;
};

const LinkCrumb = ({ label, path, onCrumbClick }) => {
    let component = null;

    if (label && path) {
        component = (
            <a
                className="eds-breadcrumbs__item--link eds-text-bm eds-text-color--grey-600"
                href={path}
                data-spec="breadcrumbs-link"
                onClick={onCrumbClick.bind(null, path)}
            >
                {label}
            </a>
        );
    }

    return component;
};

const CrumbItem = ({
    isLinkCrumb,
    isLastCrumb,
    isActive,
    path,
    label,
    onCrumbClick,
}) => {
    let component = <DisabledCrumb label={label} />;

    if (isActive) {
        component = <ActiveCrumb label={label} />;
    } else if (isLinkCrumb) {
        component = (
            <LinkCrumb path={path} onCrumbClick={onCrumbClick} label={label} />
        );
    }

    return (
        <li key={path} className="eds-breadcrumbs__item">
            {component}
            <CrumbSeparator isLastCrumb={isLastCrumb} />
        </li>
    );
};

export default class Breadcrumbs extends React.PureComponent {
    static propTypes = {
        /**
         * An array of breadcrumb objects containing url and path. Last item refers to the current directory.
         * ex. [{path: '/', label: 'Home'}, {path: '/tickets', label: 'My Tickets'}]
         */
        crumbs: CRUMBS_PROP_TYPE.isRequired,
        /**
         * A callback function that receives the path when a breadcrumb is clicked
         * When this prop is not passed, the component will redirect the user to the path by default
         * ex. (path) => {}
         */
        onCrumbClick: PropTypes.func,
    };

    _handleClick(path, e) {
        const { onCrumbClick } = this.props;

        if (onCrumbClick) {
            e.preventDefault();
            e.stopPropagation();
            onCrumbClick(path);
        }
    }

    _renderBreadcrumbs() {
        const { crumbs } = this.props;
        // change to true when the Active Crumb is created, so next crumbs are disabled
        let activeCrumbRendered = false;

        return crumbs.map((crumb, index) => {
            const { path, label, active } = crumb;

            const isLastCrumb = index === crumbs.length - 1;
            const isLinkCrumb = !(activeCrumbRendered || active);

            if (active) {
                activeCrumbRendered = active;
            }

            return (
                <CrumbItem
                    key={path}
                    onCrumbClick={this._handleClick.bind(this)}
                    path={path}
                    isActive={active}
                    label={label}
                    isLastCrumb={isLastCrumb}
                    isLinkCrumb={isLinkCrumb}
                />
            );
        });
    }

    render() {
        const breadcrumbs = this._renderBreadcrumbs();

        return <ul className="eds-breadcrumbs">{breadcrumbs}</ul>;
    }
}
